/**
 * "Waiting for your code", kept where a reload finds it (easy-signup handoff §4, 2026-10-04).
 *
 * Inside Facebook's browser the code trip is: leave for the mail app, copy the code, come back — and
 * that browser may reload the page, or reopen it without the history state the code screen was
 * given. So the code screen's state (which email, which kind of code, the sign-in session, where
 * they were going) is also kept in localStorage for the life of a code, and the code screen reads
 * it when it arrives with nothing. Cleared the moment the code is accepted.
 *
 * A sign-in code's session lasts 15 minutes (the client's AuthSessionValidity); after that only the
 * email and the place are kept, and the screen offers a fresh code.
 */
const KEY = 'saddle.pendingCode';
const MAX_AGE_MS = 60 * 60 * 1000;
const SESSION_MAX_AGE_MS = 15 * 60 * 1000;

export interface PendingCode {
  email: string;
  mode: 'signup' | 'signin';
  session?: string;
  from?: string;
  sentAt: number;
}

export function rememberPendingCode(p: PendingCode): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* storage blocked: the history state still carries it for a plain reload */
  }
}

export function pendingCode(now = Date.now()): PendingCode | null {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) ?? 'null') as PendingCode | null;
    if (!p || typeof p.email !== 'string' || !p.email || now - p.sentAt > MAX_AGE_MS) return null;
    if (p.mode === 'signin' && (!p.session || now - p.sentAt > SESSION_MAX_AGE_MS)) {
      // The sign-in session has lapsed: the email and the place are still worth keeping.
      const { session: _gone, ...rest } = p;
      return { ...rest, mode: 'signin' };
    }
    return p;
  } catch {
    return null;
  }
}

export function forgetPendingCode(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to forget */
  }
}
